import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  MessageCircle,
  Send,
  Bot,
  User,
  Sparkles,
  RefreshCw,
  Shield,
  AlertTriangle,
  Lightbulb,
  Calendar,
  Trash2,
  Loader2,
} from "lucide-react";

import api from "../lib/api";

const AIChatAssistant = ({ minimized = false, onToggle }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [securityScore, setSecurityScore] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Add welcome message
    setMessages([
      {
        role: "assistant",
        content:
          'Hi! I\'m your SecureSight AI Assistant. I can answer questions about your security posture, devices, and users. Try asking:\n\n• "Are we secure?"\n• "Which employee is highest risk?"\n• "What should I fix this week?"\n• "Why is [device name] slow?"',
        timestamp: new Date().toISOString(),
      },
    ]);

    // Generate new session ID
    setSessionId(`chat-${Date.now()}`);
  }, []);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: input,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await api.post("/chat", {
        message: input,
        session_id: sessionId,
      });

      const data = response.data;
      setSecurityScore(data.security_score);

      const assistantMessage = {
        role: "assistant",
        content: data.response,
        timestamp: data.timestamp,
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = {
        role: "assistant",
        content: "I'm sorry, I encountered an error. Please try again.",
        timestamp: new Date().toISOString(),
        error: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    }

    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        role: "assistant",
        content: "Chat cleared! How can I help you?",
        timestamp: new Date().toISOString(),
      },
    ]);
    setSessionId(`chat-${Date.now()}`);
  };

  const askQuickQuestion = (question) => {
    setInput(question);
  };

  const quickQuestions = [
    { icon: Shield, text: "Are we secure?", color: "text-green-500" },
    {
      icon: AlertTriangle,
      text: "Which employee is highest risk?",
      color: "text-orange-500",
    },
    {
      icon: Calendar,
      text: "What should I fix this week?",
      color: "text-blue-500",
    },
    { icon: Lightbulb, text: "Give me quick wins", color: "text-yellow-500" },
  ];

  if (minimized) {
    return (
      <Button
        onClick={onToggle}
        className="fixed bottom-4 right-4 h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700"
        data-testid="chat-toggle-btn"
      >
        <MessageCircle className="h-6 w-6" />
      </Button>
    );
  }

  return (
    <Card
      className="fixed bottom-4 right-4 w-96 h-[600px] shadow-2xl flex flex-col z-50"
      data-testid="ai-chat-panel"
    >
      {/* Header */}
      <CardHeader className="pb-2 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <Bot className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">SecureSight AI</CardTitle>
              <p className="text-xs text-white/80">Your security assistant</p>
            </div>
          </div>
          <div className="flex gap-1">
            {securityScore !== null && (
              <Badge variant="secondary" className="text-xs">
                Score: {securityScore}
              </Badge>
            )}
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-white hover:bg-white/20"
              onClick={clearChat}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            {onToggle && (
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-white hover:bg-white/20"
                onClick={onToggle}
              >
                ×
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-lg p-3 ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : msg.error
                      ? "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
                      : "bg-muted"
                }`}
              >
                <div className="flex items-start gap-2">
                  {msg.role === "assistant" && (
                    <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-purple-500" />
                  )}
                  <div className="text-sm whitespace-pre-wrap">
                    {msg.content}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
                  <span className="text-sm text-muted-foreground">
                    Thinking...
                  </span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Quick Questions */}
      {messages.length <= 2 && (
        <div className="px-4 pb-2">
          <p className="text-xs text-muted-foreground mb-2">Quick questions:</p>
          <div className="flex flex-wrap gap-1">
            {quickQuestions.map((q, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                className="text-xs h-7"
                onClick={() => askQuickQuestion(q.text)}
              >
                <q.icon className={`h-3 w-3 mr-1 ${q.color}`} />
                {q.text}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about your security..."
            disabled={loading}
            className="flex-1"
            data-testid="chat-input"
          />
          <Button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            size="icon"
            data-testid="chat-send-btn"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default AIChatAssistant;
